/* eslint-disable react-hooks/exhaustive-deps */
import React, {useCallback, useEffect, useRef, useState} from "react"
import {useTranslation} from "react-i18next"
import {Checkbox, Popover} from "antd"
import {Icon} from "components/Icon"
import {BasePopup} from "components/popup"
import {BaseInput} from "components/inputs"
import {BaseButton, SecondaryButton} from "components/buttons"
import styles from "./PopupEditMultipleAttendance.module.css"
import {attendanceService, profileService} from "services"
import {cloneDeep, get} from "lodash"
import {convertTZ, formatDateOnly, getFullName, getUserPhotoUrl, handleError, toastSuccess, toastWarning} from "helpers"
import moment from "moment-timezone"
import {BaseLoading} from "components"
import {useCurrentProfilePermissions, useModel, useVisible} from "hooks"
import {AttendanceEvent, AttendanceEventType, AttendanceStatus} from "types/attendance"
import cx from "classnames"
import {BaseTimePicker} from "components/DateTimePicker"
import {v4 as uuid} from "uuid"
import {UserGeolocationMap} from "./UserGeolocationMap"
import {ConfirmPopup} from "uiKit"
import {useMutation} from "@tanstack/react-query"
import {UNLIMITED_PAGE_SIZE} from "data/constants"
import {YesNo} from "types/common"
import {Auth} from "types/auth"
import {Permissions} from "@edular/permissions"

const EVENT_OPTIONS: Array<{name: string; value: AttendanceEventType}> = [
    {name: "Checkin", value: AttendanceEventType.Checkin},
    {name: "Checkout", value: AttendanceEventType.Checkout},
    {name: "In", value: AttendanceEventType.BreakEnd},
    {name: "Out", value: AttendanceEventType.BreakStart}
]

type Props = {
    isShow: boolean
    selectedAttendances?: any[]
    isExternshipAttendance?: boolean
    getListAttendances?: () => void
    clearAllSelectedAttendance: () => void
    onClose: () => void
}

export function PopupEditMultipleAttendance(props: Props) {
    const {
        isShow,
        onClose,
        getListAttendances,
        clearAllSelectedAttendance,
        selectedAttendances,
        isExternshipAttendance = false
    } = props
    const deleteEventIds = useRef<number[]>([])
    const [checkInTime, setCheckInTime] = useState<any>()
    const [checkOutTime, setCheckOutTime] = useState<any>()
    const {t} = useTranslation(["studentServices"])
    const [isLoading, setIsLoading] = useState(false)
    const [showPopover, setShowPopover] = useState({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [listOfStudentProfile, setListOfStudentProfile] = useState<any>()
    const [listAttendances, setListAttendances] = useState<any>([])
    const [geoLocation, setGeoLocation] = useState({latitude: null, longitude: null})
    const model = useModel()
    const timeFormat = model.getUserTimeFormat()
    const dateFormat = model.getUserDateFormat()
    const userGeolocationMapPopup = useVisible()
    const [isMapDisplayedAtLeastOnce, setIsMapDisplayedAtLeastOnce] = useState(false)
    const confirmDeletePopup = useVisible()
    const currentProfilePermissions = useCurrentProfilePermissions()

    const canDeleteAttendance = currentProfilePermissions.hasPermissions({
        staff: [Permissions.Staff.AcademicAffairs.StudentServices.Attendance.Delete]
    })

    const canAddAttendance = currentProfilePermissions.hasPermissions({
        staff: [Permissions.Staff.AcademicAffairs.StudentServices.Attendance.Add]
    })

    const getAvatarUrl = (studentInfo: any) =>
        studentInfo?.photo ? getUserPhotoUrl(studentInfo?.photo, 512) : "/image/DefaultAvatar.png"

    const deleteDailyAttendanceMutation = useMutation(attendanceService.deleteDailyAttendance, {
        onSuccess: () => {
            toastSuccess("Deleted successfully")
            getListAttendances()
            clearAllSelectedAttendance()
            resetForm()
        },
        onError: (err) => {
            handleError(err, true)
        }
    })

    const loading = isSubmitting || deleteDailyAttendanceMutation.isLoading

    const getStudentInfo = useCallback(async () => {
        const {data: student} = await profileService.getAll({
            filter: {
                id: selectedAttendances.map((item) => item.profileId),
                type: Auth.UserProfileType.Student
            }
        })
        setListOfStudentProfile(student)
    }, [selectedAttendances])

    const getAttendanceDetail = useCallback(async () => {
        setIsLoading(true)
        try {
            const params = {
                attendanceIds: selectedAttendances.map((item) => item.attendanceId),
                includeAttendanceDeletedEvents: model.isStaffOrAdmin(),
                includeDeletedAttendance: model.isStaffOrAdmin()
            }
            let data: any = []
            if (isExternshipAttendance) {
                data = selectedAttendances
            } else {
                const {data: attendanceData} = await attendanceService.getDailyAttendanceList({
                    range: {
                        page: 1,
                        pageSize: UNLIMITED_PAGE_SIZE
                    },
                    ...params
                })
                data = attendanceData
            }
            if (data.length) {
                const attendances = data.map((attendance) => {
                    const events = (attendance.events ?? []).sort(function (left, right) {
                        return moment.utc(left.createdAt).diff(moment.utc(right.createdAt))
                    })
                    const checkinnEvent = events.find(
                        (item) => item.eventType === AttendanceEventType.Checkin && !item.deletedAt
                    )
                    const checkoutEvent = events.find(
                        (item) => item.eventType === AttendanceEventType.Checkout && !item.deletedAt
                    )

                    const timezone = get(attendance, ["student", "timezone"], moment.tz.guess())

                    const status = attendance.status
                    let isVerified = attendance.isVerified === YesNo.Yes
                    if (status === AttendanceStatus.Approved && isVerified) {
                        isVerified = true
                    } else if (status === AttendanceStatus.Rejected && isVerified) {
                        isVerified = false
                    } else {
                        isVerified = null
                    }

                    return {
                        ...attendance,
                        timezone,
                        status,
                        isVerified,
                        isMakeup: !!attendance.isMakeup,
                        isExcused: attendance.isExcused,
                        dateData: {
                            checkInTime: checkinnEvent
                                ? moment(convertTZ(new Date(checkinnEvent.createdAt), timezone)).format(timeFormat)
                                : "",
                            checkOutTime: checkoutEvent
                                ? moment(convertTZ(new Date(checkoutEvent.createdAt), timezone)).format(timeFormat)
                                : "",
                            checkInDate: checkinnEvent
                                ? moment(convertTZ(new Date(checkinnEvent.createdAt), timezone)).format(dateFormat)
                                : ""
                        },
                        events: events.map((event) => {
                            return {
                                ...event,
                                createdAt: moment(event.createdAt).tz(timezone)
                            }
                        })
                    }
                })

                setListAttendances(attendances)
            }
        } catch (err) {
            handleError(err)
        } finally {
            setIsLoading(false)
        }
    }, [selectedAttendances, listAttendances, isExternshipAttendance, model])

    useEffect(() => {
        if (isShow && selectedAttendances?.length) {
            getStudentInfo()
            getAttendanceDetail()
        }
    }, [isShow, selectedAttendances])

    useEffect(() => {
        if (!isShow) {
            setListOfStudentProfile([])
            deleteEventIds.current = []
        }
    }, [isShow])

    useEffect(() => {
        if (userGeolocationMapPopup.isVisible && !isMapDisplayedAtLeastOnce) {
            setIsMapDisplayedAtLeastOnce(true)
        }
    }, [userGeolocationMapPopup.isVisible, isMapDisplayedAtLeastOnce])

    const renderStudentInfo = (studentInfo: any) => {
        return (
            <div className={styles.studentWrapper}>
                <div className={styles.imgWrapper}>
                    <img src={getAvatarUrl(studentInfo)} className={styles.avatar} alt="" />
                </div>
                <div>
                    <p className={styles.studentName}>{getFullName(studentInfo)}</p>
                    <div className={styles.flexContainer}>
                        <div className={styles.columnContainer}>
                            <div className={styles.labelContainer}>
                                <span className={styles.idLabel}>{t("popup.userId")}: </span>
                                <span className={styles.idValue}>{studentInfo?.customUserId}</span>
                            </div>
                            <div className={styles.labelContainer}>
                                <span className={styles.idLabel}>{t("popup.enrollmentId")}: </span>
                                <span className={styles.idValue}>{studentInfo?.customProfileId}</span>
                            </div>
                        </div>

                        <div className={styles.labelContainer}></div>
                    </div>
                </div>
            </div>
        )
    }

    const renderTitle = (eventType: string) => {
        if (eventType === "check-out") {
            return t("popup.checkout")
        } else if (eventType === "check-in") {
            return t("popup.checkin")
        } else if (eventType === "break-start") {
            return "Out"
        } else if (eventType === "break-end") {
            return "In"
        } else if (eventType === "lessonStart") {
            return "Lesson Start"
        } else if (eventType === "lessonEnd") {
            return "Lesson End"
        } else if (eventType === "attendance-check") {
            return "In"
        } else {
            return "Select Type"
        }
    }

    const renderIconAttendance = (eventType: string, status: string) => {
        if (eventType === "check-out") {
            return (
                <Icon
                    icon="CHECKOUT_FILL"
                    color={status === "success" ? "#1E90FF" : "#E6E6E6"}
                    className={styles.checkOutIcon}
                />
            )
        } else if (eventType === "check-in") {
            return (
                <Icon
                    icon="CHECKIN_FILL"
                    color={status === "success" ? "#1E90FF" : "#E6E6E6"}
                    className={styles.rightIcon}
                />
            )
        } else if (eventType === "break-start") {
            return <Icon icon="ATTENDANCE_OUT" color={"#DF1642"} className={styles.rightIcon} />
        } else if (eventType === "break-end") {
            return <Icon icon="ATTENDANCE_IN" color={"#18A957"} className={styles.rightIcon} />
        } else if (eventType === "attendance-check") {
            return (
                <Icon
                    icon="ATTENDANCE_IN"
                    color={status === "success" ? "#18A957" : "#E6E6E6"}
                    className={styles.rightIcon}
                />
            )
        } else {
            return <Icon icon="ARROW_DOWN" color={"grey"} className={styles.arrowIcon} />
        }
    }

    const onPressDeleteEvent = (event: AttendanceEvent) => {
        typeof event.id === "number" && deleteEventIds.current.push(event.id)
        const attendances = listAttendances.map((item) => {
            const events = item.events.filter((e) => e.id !== event.id)
            return {
                ...item,
                events
            }
        })
        setListAttendances([...attendances])
    }

    const onAddEvent = (attendanceId) => {
        const attendance = listAttendances.find((item) => item.id === attendanceId)
        attendance.events.push({
            status: "success",
            attendanceId,
            timezone: attendance.timezone,
            id: uuid()
        })
        setListAttendances(cloneDeep(listAttendances))
    }

    const renderAddEvent = (attendanceId: number, canAddAttendance) => {
        return (
            <div className={styles.buttonAddEvent}>
                <BaseButton disabled={!canAddAttendance} title="Add Event" onClick={() => onAddEvent(attendanceId)} />
            </div>
        )
    }

    const renderPopoverSelectEvent = (event: AttendanceEvent) => {
        const attendance = listAttendances.find((item) => item.id === event.attendanceId)
        const checkIn = attendance.events.find(
            (item) => item.eventType === AttendanceEventType.Checkin && item.status === "success" && !item.deletedAt
        )

        const checkOut = attendance.events.find(
            (item) => item.eventType === AttendanceEventType.Checkout && item.status === "success" && !item.deletedAt
        )

        let options = cloneDeep(EVENT_OPTIONS)

        if (event?.eventType) {
            options = options.filter((item) => item.value !== event.eventType)
        }

        if (checkIn) {
            options = options.filter((item) => item.value !== AttendanceEventType.Checkin)
        }

        if (checkOut) {
            options = options.filter((item) => item.value !== AttendanceEventType.Checkout)
        }

        return (
            <div className={styles.selectContainer}>
                {options.map((option) => (
                    <div
                        onClick={() => {
                            attendance.events = attendance.events.map((e) => {
                                if (e.id === event.id) {
                                    e.eventType = option.value
                                }
                                return e
                            })
                            setListAttendances(cloneDeep(listAttendances))
                            showPopover[event.id] = false
                            setShowPopover({...showPopover})
                        }}
                        className={cx(styles.popupContainer, styles.hoverBackgroundGrey)}>
                        {renderIconAttendance(option.value, "success")}
                        <span style={{width: 120}} className={styles.infoLabel}>
                            {renderTitle(option.value)}
                        </span>
                    </div>
                ))}
            </div>
        )
    }

    const renderCheckInItem = (event: any) => {
        const isDeleted = !!event?.deletedAt
        const isRenderDelete = event?.status === "success" && event?.canDeleteAttendance && !isDeleted
        const geoLocation = event?.jsonData?.geoLocation
        const attendance = listAttendances.find((item) => item.id === event.attendanceId)

        return (
            <div
                key={event?.id ?? uuid()}
                style={{
                    opacity: isDeleted ? 0.5 : 1,
                    pointerEvents: isDeleted ? "none" : "auto"
                }}>
                <div className={styles.infoWrapper}>
                    {event?.status === "success" ? (
                        <>
                            <BaseTimePicker
                                style={{width: 130, marginRight: 24}}
                                value={
                                    event?.createdAt
                                        ? moment(convertTZ(new Date(event?.createdAt), event.timezone))
                                        : ""
                                }
                                disabled={!!event?.id && !event?.canEditAttendance}
                                format={timeFormat}
                                allowClear={false}
                                onChange={(date) => {
                                    attendance.events = attendance.events.map((e) => {
                                        if (e.id === event.id) {
                                            e.createdAt = moment(date).tz(event.timezone, true)
                                        }
                                        return e
                                    })
                                    setListAttendances(cloneDeep(listAttendances))
                                }}
                                placeholder="Time"
                            />
                        </>
                    ) : (
                        <div style={{backgroundColor: "transparent"}} className={styles.hourLabel} />
                    )}
                    {event?.status === "success" ? (
                        <Popover
                            placement="right"
                            color="white"
                            visible={showPopover[event.id]}
                            arrowContent={null}
                            onVisibleChange={(visible) => {
                                showPopover[event.id] = visible
                                setShowPopover({...showPopover})
                            }}
                            content={() => renderPopoverSelectEvent(event)}
                            trigger={!!event?.id && !event?.canEditAttendance ? [] : "click"}>
                            <div className={styles.popupContainer}>
                                {renderIconAttendance(event?.eventType, event.status)}
                                <div className={styles.eventName}>
                                    <span
                                        style={{width: 80, color: event?.eventType ? "black" : "rgba(0,0,0,0.7)"}}
                                        className={styles.infoLabel}>
                                        {renderTitle(event?.eventType)}
                                    </span>
                                    {typeof event?.id === "number" ? (
                                        <>
                                            <div className={styles.eventInfo}>
                                                <span className={styles.eventId}>ID: {event.id} </span>
                                                {event.synced ? (
                                                    <div className={styles.syncedIcon}>
                                                        <Icon
                                                            icon="APPROVE"
                                                            size={12}
                                                            color={"var(--success-400-base)"}
                                                        />
                                                    </div>
                                                ) : null}
                                            </div>
                                            {event.sisId?.length ? (
                                                <div className={styles.eventInfo}>
                                                    <span className={styles.eventId}>ExID: {event.sisId} </span>
                                                </div>
                                            ) : null}
                                        </>
                                    ) : null}
                                </div>
                            </div>
                        </Popover>
                    ) : (
                        <div className={styles.eventName}>
                            <span style={{width: 80}} className={styles.infoLabel}>
                                {renderTitle(event?.eventType)}
                            </span>

                            {typeof event?.id === "number" ? (
                                <>
                                    <div className={styles.eventInfo}>
                                        <span className={styles.eventId}>ID: {event.id} </span>
                                        {event.synced ? (
                                            <div className={styles.syncedIcon}>
                                                <Icon icon="APPROVE" size={12} color={"var(--success-400-base)"} />
                                            </div>
                                        ) : null}
                                    </div>
                                    {event.sisId?.length ? (
                                        <div className={styles.eventInfo}>
                                            <span className={styles.eventId}>ExID: {event.sisId} </span>
                                        </div>
                                    ) : null}
                                </>
                            ) : null}
                        </div>
                    )}

                    <div className={styles.applyRoundingContainer}>
                        <Checkbox
                            checked={event?.applyRounding}
                            disabled={!!event?.id && !event?.canEditAttendance}
                            onChange={(e) => {
                                attendance.events = attendance.events.map((e) => {
                                    if (e.id === event.id) {
                                        e.applyRounding = !e.applyRounding
                                    }
                                    return e
                                })
                                setListAttendances(cloneDeep(listAttendances))
                            }}>
                            <span className={styles.infoLabel}>Apply Rounding</span>
                        </Checkbox>
                        {event?.createdByUserName?.length ? (
                            <span className={styles.eventId}>Created By: {event.createdByUserName} </span>
                        ) : null}
                        {event?.modifiedByUserName?.length ? (
                            <span className={styles.eventId}>Modified By: {event.modifiedByUserName} </span>
                        ) : null}
                        {isDeleted ? (
                            <span className={styles.eventId}>
                                Deleted At: {moment(event.deletedAt).format(model.getUserDateTimeFormat())}{" "}
                            </span>
                        ) : null}
                    </div>

                    {event?.status === "success" ? (
                        <BaseInput
                            value={event?.message ?? ""}
                            disabled={!!event?.id && !event?.canEditAttendance}
                            onChange={(text) => {
                                attendance.events = attendance.events.map((e) => {
                                    if (e.id === event.id) {
                                        e.message = text
                                    }
                                    return e
                                })
                                setListAttendances(cloneDeep(listAttendances))
                            }}
                            placeholder={t("popup.notes")}
                            className={styles.inputWidth}
                        />
                    ) : (
                        <div className={styles.inputWidth} />
                    )}
                    {!!geoLocation && (
                        <div
                            onClick={() => {
                                setGeoLocation(geoLocation)
                                userGeolocationMapPopup.open()
                            }}>
                            <Icon icon="LOCATION" className={styles.deleteIcon} />
                        </div>
                    )}
                    {isRenderDelete ? (
                        <div onClick={() => onPressDeleteEvent(event)}>
                            <Icon color={"var(--error-400-base)"} icon="DELETE" className={styles.deleteIcon} />
                        </div>
                    ) : null}
                </div>
            </div>
        )
    }

    const resetForm = useCallback(() => {
        getListAttendances?.()
        setListOfStudentProfile([])
        deleteEventIds.current = []
        setListAttendances([])
        onClose()
        setCheckInTime(undefined)
        setCheckOutTime(undefined)
    }, [getListAttendances])

    const onConfirmDeleteAttendance = useCallback(() => {
        const attendanceIds = selectedAttendances.map((item) => item.attendanceId)
        deleteDailyAttendanceMutation.mutate({
            attendanceIds
        })
        confirmDeletePopup.close()
    }, [selectedAttendances])

    const onSave = useCallback(async () => {
        setIsSubmitting(true)
        try {
            if (checkInTime || checkOutTime) {
                const params = listAttendances.map((attendance) => {
                    const listEvent = attendance.events ?? []

                    const events = listEvent
                        .filter((item) => typeof item.id === "number" && !item.deletedAt)
                        .map((item) => {
                            let createdAt = moment(item.createdAt).tz(attendance.timezone, true)
                            if (checkInTime && item.eventType === AttendanceEventType.Checkin) {
                                createdAt = moment(checkInTime).tz(attendance.timezone, true)
                            }

                            if (checkOutTime && item.eventType === AttendanceEventType.Checkout) {
                                createdAt = moment(checkOutTime).tz(attendance.timezone, true)
                            }

                            const startDate = moment(attendance.date).tz(attendance.timezone, true)
                            createdAt.set({
                                month: startDate.month(),
                                date: startDate.date(),
                                year: startDate.year()
                            })
                            return {
                                id: item.id,
                                applyRounding: !!item.applyRounding,
                                message: item.message ?? "",
                                eventType: item.eventType,
                                createdAt: createdAt.utc().toISOString()
                            }
                        })
                    const checkinEvent = events.find((item) => item.eventType === AttendanceEventType.Checkin)
                    const checkoutEvent = events.find((item) => item.eventType === AttendanceEventType.Checkout)
                    const listEventAdd = []
                    if (!checkinEvent && checkInTime) {
                        const createdAt = moment(checkInTime).tz(attendance.timezone, true)
                        const startDate = moment(attendance.date).tz(attendance.timezone, true)
                        createdAt.set({
                            month: startDate.month(),
                            date: startDate.date(),
                            year: startDate.year()
                        })
                        listEventAdd.push({
                            message: "",
                            eventType: AttendanceEventType.Checkin,
                            status: "success",
                            attendanceId: attendance.id,
                            createdAt: createdAt.utc().toISOString()
                        })
                    }

                    if (!checkoutEvent && checkOutTime) {
                        const createdAt = moment(checkOutTime).tz(attendance.timezone, true)
                        const startDate = moment(attendance.date).tz(attendance.timezone, true)
                        createdAt.set({
                            month: startDate.month(),
                            date: startDate.date(),
                            year: startDate.year()
                        })
                        listEventAdd.push({
                            message: "",
                            eventType: AttendanceEventType.Checkout,
                            status: "success",
                            attendanceId: attendance.id,
                            createdAt: createdAt.utc().toISOString()
                        })
                    }

                    let status = attendance.status

                    if (attendance.isVerified === true) {
                        status = AttendanceStatus.Approved
                    } else if (attendance.isVerified === false) {
                        status = AttendanceStatus.Rejected
                    }

                    return {
                        events,
                        addEvents: listEventAdd,
                        deleteEventIds: deleteEventIds.current,

                        attendanceId: attendance.id,
                        isMakeup: !!attendance.isMakeup,
                        distEdEnabled: !!attendance.distEdEnabled,
                        status: status,
                        isVerified: [AttendanceStatus.Approved, AttendanceStatus.Rejected].includes(status)
                            ? true
                            : undefined
                    }
                })

                await attendanceService.updateDailyAttendance(params)
            } else {
                for (let attendance of listAttendances) {
                    const listEvent = attendance.events ?? []
                    const listEventAdd = listEvent.filter((item) => typeof item.id !== "number")

                    if (listEventAdd?.length && listEventAdd.find((event) => !event.eventType)) {
                        setIsSubmitting(false)
                        return toastWarning(
                            "You need to select the event type: Check-in/Check-out or In/Out for all events"
                        )
                    }

                    if (listEventAdd?.length && listEventAdd.find((event) => !event.createdAt)) {
                        setIsSubmitting(false)
                        return toastWarning("You need to select the time for all events")
                    }
                }

                const params: any = listAttendances.map((attendance) => {
                    if (attendance.status === "approved") {
                        attendance.isExcusedAbsence = false
                    }
                    const listEvent = attendance.events ?? []
                    const listEventAdd = listEvent
                        .filter((item) => typeof item.id !== "number")
                        .map((item) => {
                            const createdAt = moment(item.createdAt).tz(attendance.timezone, true)
                            const startDate = moment(attendance.date).tz(attendance.timezone, true)
                            createdAt.set({
                                month: startDate.month(),
                                date: startDate.date(),
                                year: startDate.year()
                            })
                            return {
                                message: item.message ?? "",
                                applyRounding: !!item.applyRounding,
                                eventType: item.eventType,
                                status: "success",
                                attendanceId: attendance.id,
                                createdAt: createdAt.utc().toISOString()
                            }
                        })

                    const events = listEvent
                        .filter((item) => typeof item.id === "number" && !item.deletedAt)
                        .map((item) => {
                            const createdAt = moment(item.createdAt).tz(attendance.timezone, true)
                            const startDate = moment(attendance.date).tz(attendance.timezone, true)
                            createdAt.set({
                                month: startDate.month(),
                                date: startDate.date(),
                                year: startDate.year()
                            })
                            return {
                                id: item.id,
                                applyRounding: !!item.applyRounding,
                                message: item.message ?? "",
                                eventType: item.eventType,
                                createdAt: createdAt.utc().toISOString()
                            }
                        })

                    let status = attendance.status

                    if (attendance.isVerified === true) {
                        status = AttendanceStatus.Approved
                    } else if (attendance.isVerified === false) {
                        status = AttendanceStatus.Rejected
                    }

                    return {
                        events,
                        addEvents: listEventAdd,
                        deleteEventIds: deleteEventIds.current,
                        attendanceId: attendance.id,
                        isMakeup: !!attendance.isMakeup,
                        distEdEnabled: !!attendance.distEdEnabled,
                        status: status,
                        isVerified: [AttendanceStatus.Approved, AttendanceStatus.Rejected].includes(status)
                            ? true
                            : undefined
                    }
                })

                await attendanceService.updateDailyAttendance(params)
            }
            resetForm()
            clearAllSelectedAttendance()
            toastSuccess("Updated successfully")
        } catch (err) {
            handleError(err)
        } finally {
            setIsSubmitting(false)
        }
    }, [resetForm, listAttendances, clearAllSelectedAttendance, checkInTime, checkOutTime])

    return (
        <BasePopup
            isShow={isShow}
            onClose={() => {
                setCheckInTime(undefined)
                setCheckOutTime(undefined)
                onClose()
            }}
            leftIcon="PERSON_FILL"
            width="70vw">
            <div className={styles.popupWrapper}>
                <p className={styles.title}>{t("attendance.title")}</p>
                <div className={styles.manualTime}>
                    <div className={styles.bulkEvent}>
                        <span className={styles.bulkTitle}>Check-in</span>
                        <BaseTimePicker
                            style={{width: 130, marginRight: 24}}
                            value={checkInTime}
                            disabled={!canAddAttendance}
                            format={timeFormat}
                            allowClear={false}
                            onChange={(date) => {
                                setCheckInTime(moment(date))
                            }}
                            placeholder="Time"
                        />
                    </div>
                    <div className={styles.bulkEvent}>
                        <span className={styles.bulkTitle}>Check-out</span>
                        <BaseTimePicker
                            style={{width: 130, marginRight: 24}}
                            value={checkOutTime}
                            disabled={!canAddAttendance}
                            format={timeFormat}
                            allowClear={false}
                            onChange={(date) => {
                                setCheckOutTime(moment(date))
                            }}
                            placeholder="Time"
                        />
                    </div>
                </div>

                {listAttendances.map((attendance) => {
                    const canEditAttendance =
                        currentProfilePermissions.hasPermissions({
                            staff: [Permissions.Staff.AcademicAffairs.StudentServices.Attendance.Edit]
                        }) &&
                        !attendance?.isLocked &&
                        !attendance?.deletedAt

                    const canDeleteAttendance =
                        currentProfilePermissions.hasPermissions({
                            staff: [Permissions.Staff.AcademicAffairs.StudentServices.Attendance.Delete]
                        }) &&
                        !attendance?.isLocked &&
                        !attendance?.deletedAt

                    const canAddAttendance =
                        currentProfilePermissions.hasPermissions({
                            staff: [Permissions.Staff.AcademicAffairs.StudentServices.Attendance.Add]
                        }) &&
                        !attendance?.isLocked &&
                        !attendance?.deletedAt

                    const studentInfo = listOfStudentProfile.find((profile) => profile.id === attendance.profileId)
                    const timezone = get(attendance, ["student", "timezone"], moment.tz.guess())

                    const checkInEvent = attendance.events.filter(
                        (event) =>
                            event.eventType === AttendanceEventType.Checkin &&
                            event.status === "success" &&
                            !event.deletedAt
                    )
                    const checkOutEvent = attendance.events.filter(
                        (event) =>
                            event.eventType === AttendanceEventType.Checkout &&
                            event.status === "success" &&
                            !event.deletedAt
                    )
                    const otherEvents = attendance.events.filter(
                        (event) =>
                            (event.eventType !== AttendanceEventType.Checkout &&
                                event.eventType !== AttendanceEventType.Checkin) ||
                            !!event.deletedAt
                    )

                    return (
                        <>
                            {attendance.date ? (
                                <div>
                                    <div className={styles.timeWrapper}>
                                        <span>{formatDateOnly(attendance?.date, dateFormat)}</span>
                                    </div>
                                </div>
                            ) : null}

                            <hr className={styles.divisor} />

                            {studentInfo && renderStudentInfo(studentInfo)}

                            {attendance.id ? (
                                <>
                                    {checkInEvent.map((item) =>
                                        renderCheckInItem({
                                            status: "failure",
                                            eventType: AttendanceEventType.Checkin,
                                            timezone: attendance.timezone,
                                            attendanceId: attendance.id,
                                            ...item,
                                            canEditAttendance,
                                            canDeleteAttendance,
                                            canAddAttendance
                                        })
                                    )}
                                    {otherEvents.map((item) =>
                                        renderCheckInItem({
                                            ...item,
                                            attendanceId: attendance.id,
                                            timezone: attendance.timezone,
                                            canEditAttendance,
                                            canDeleteAttendance,
                                            canAddAttendance
                                        })
                                    )}
                                    {checkOutEvent.map((item) =>
                                        renderCheckInItem({
                                            status: "failure",
                                            eventType: AttendanceEventType.Checkout,
                                            timezone: attendance.timezone,
                                            attendanceId: attendance.id,
                                            ...item,
                                            canEditAttendance,
                                            canDeleteAttendance,
                                            canAddAttendance
                                        })
                                    )}
                                    {renderAddEvent(attendance.id, canAddAttendance)}

                                    <div className={`${styles.flexContainer} ${styles.alignItems}`}>
                                        <BaseInput
                                            value={attendance.message ?? ""}
                                            onChange={(value) => {
                                                const newAttendances = listAttendances.map((item) => {
                                                    if (attendance.id === item.id) {
                                                        return {
                                                            ...item,
                                                            message: value
                                                        }
                                                    }
                                                    return item
                                                })
                                                setListAttendances(cloneDeep(newAttendances))
                                            }}
                                            disabled={!canEditAttendance}
                                            placeholder={t("popup.notes")}
                                        />
                                    </div>
                                    <hr className={styles.attendanceDivider} />

                                    {!isExternshipAttendance && (
                                        <>
                                            <div
                                                style={{
                                                    cursor: !canEditAttendance ? "not-allowed" : "pointer",
                                                    pointerEvents: !canEditAttendance ? "none" : "auto",
                                                    opacity: !canEditAttendance ? 0.7 : 1
                                                }}
                                                className={styles.optionsWrap}>
                                                <div className={styles.approveAttendWrap}>
                                                    <div
                                                        className={styles.attendedBodyItem}
                                                        onClick={() => {
                                                            const newAttendances = listAttendances.map((item) => {
                                                                if (attendance.id === item.id) {
                                                                    return {
                                                                        ...item,
                                                                        isVerified: true
                                                                    }
                                                                }
                                                                return item
                                                            })
                                                            setListAttendances(cloneDeep(newAttendances))
                                                        }}>
                                                        <span className={styles.checkLabel}>Approve</span>
                                                        <div className={styles.unchecked}>
                                                            <Icon
                                                                icon="APPROVE"
                                                                size={24}
                                                                color={
                                                                    attendance.isVerified === true
                                                                        ? "var(--success-400-base)"
                                                                        : "grey"
                                                                }
                                                            />
                                                        </div>
                                                    </div>

                                                    <div
                                                        className={styles.attendedBodyItem}
                                                        onClick={() => {
                                                            const newAttendances = listAttendances.map((item) => {
                                                                if (attendance.id === item.id) {
                                                                    return {
                                                                        ...item,
                                                                        isVerified: false
                                                                    }
                                                                }
                                                                return item
                                                            })
                                                            setListAttendances(cloneDeep(newAttendances))
                                                        }}>
                                                        <span className={styles.checkLabel}>Reject</span>

                                                        <div className={styles.unchecked}>
                                                            <Icon
                                                                icon="CROSS_CIRCLE"
                                                                size={24}
                                                                color={
                                                                    attendance.isVerified === false
                                                                        ? "var(--error-400-base)"
                                                                        : "grey"
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className={styles.makeupDistEdWrap}>
                                                    <div
                                                        className={styles.approveAttendWrap}
                                                        onClick={() => {
                                                            const newAttendances = listAttendances.map((item) => {
                                                                if (attendance.id === item.id) {
                                                                    return {
                                                                        ...item,
                                                                        distEdEnabled: !item.distEdEnabled
                                                                    }
                                                                }
                                                                return item
                                                            })
                                                            setListAttendances(cloneDeep(newAttendances))
                                                        }}>
                                                        <span className={styles.checkLabel}>DistEd</span>
                                                        <div
                                                            className={cx(styles.uncheckedMakeup, {
                                                                [styles.checked]: attendance.distEdEnabled
                                                            })}>
                                                            <Icon
                                                                icon="TICK"
                                                                className={styles.attendanceTickIcon}
                                                                color="#e5e5e5"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div
                                                        className={styles.approveAttendWrap}
                                                        onClick={() => {
                                                            const newAttendances = listAttendances.map((item) => {
                                                                if (attendance.id === item.id) {
                                                                    return {
                                                                        ...item,
                                                                        isMakeup: !item.isMakeup
                                                                    }
                                                                }
                                                                return item
                                                            })
                                                            setListAttendances(cloneDeep(newAttendances))
                                                        }}>
                                                        <span className={styles.checkLabel}>Makeup</span>
                                                        <div
                                                            className={cx(styles.uncheckedMakeup, {
                                                                [styles.checked]: attendance.isMakeup
                                                            })}>
                                                            <Icon
                                                                icon="TICK"
                                                                className={styles.attendanceTickIcon}
                                                                color="#e5e5e5"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            {attendance?.deletedAt ? (
                                                <div
                                                    className={styles.extraData}
                                                    style={{flexDirection: "column", alignItems: "flex-start"}}>
                                                    <span
                                                        className={styles.eventId}
                                                        style={{
                                                            fontSize: 16,
                                                            color: "red"
                                                        }}>
                                                        {attendance.deletedBy
                                                            ? `Deleted By: ${attendance.deletedBy} `
                                                            : ""}
                                                        at:{" "}
                                                        {moment(
                                                            convertTZ(new Date(attendance.deletedAt), timezone)
                                                        ).format(`${dateFormat} ${timeFormat}`)}
                                                    </span>
                                                </div>
                                            ) : null}
                                        </>
                                    )}
                                </>
                            ) : (
                                <div className={styles.emptyView}>
                                    <span className={styles.textEmpty}>Attendance is empty</span>
                                </div>
                            )}
                        </>
                    )
                })}

                {model.isStaffOrAdmin() ? (
                    <div className={styles.saveContainer}>
                        <SecondaryButton
                            loading={loading}
                            disabled={!canDeleteAttendance}
                            onClick={confirmDeletePopup.open}
                            title={"Delete"}
                        />
                        <BaseButton loading={loading} onClick={onSave} title={t("popup.save")} />
                    </div>
                ) : null}

                <BaseLoading isShow={isLoading} />
                <BasePopup
                    width="600px"
                    isShow={userGeolocationMapPopup.isVisible}
                    onClose={userGeolocationMapPopup.close}>
                    {isMapDisplayedAtLeastOnce && (
                        <div style={{padding: 20}}>
                            <UserGeolocationMap geoLocation={geoLocation} />
                        </div>
                    )}
                </BasePopup>

                <ConfirmPopup
                    isVisible={confirmDeletePopup.isVisible}
                    title={"Are you sure you want to delete these attendances?"}
                    onClose={confirmDeletePopup.close}
                    onConfirm={onConfirmDeleteAttendance}
                />
            </div>
        </BasePopup>
    )
}
